import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, Space, message, Switch, Tag, Modal, Form, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import './masters-config.css';

const { Option } = Select;

const BodyPartsMaster = () => {
  const [bodyPartsList, setBodyPartsList] = useState({ data: [], loading: false, error: null });
  const [modalityOptions, setModalityOptions] = useState([]);
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalityFilter, setModalityFilter] = useState('all');

  const [form] = Form.useForm();

  const fetchBodyParts = useCallback(async () => {
    setBodyPartsList(prev => ({ ...prev, loading: true, error: null }));
    try {
      // all=true returns active + inactive so admins can manage everything.
      const response = await makeGetCall('/body-parts?all=true');
      if (response?.data?.success) {
        setBodyPartsList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch body parts');
      }
    } catch (error) {
      setBodyPartsList({ data: [], loading: false, error: error.message || 'Failed to fetch body parts' });
      message.error('Failed to fetch body parts');
    }
  }, []);

  const fetchModalities = useCallback(async () => {
    setModalitiesLoading(true);
    try {
      // Only active modalities are valid targets for a new body part.
      const response = await makeGetCall('/modalities');
      if (response?.data?.success) {
        setModalityOptions(response.data.data || []);
      }
    } catch (error) {
      // Non-fatal: body parts can still be managed, the modality dropdown just stays empty.
      console.error('Failed to fetch modalities:', error);
    } finally {
      setModalitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBodyParts();
    fetchModalities();
  }, [fetchBodyParts, fetchModalities]);

  const modalitySelectOptions = useMemo(
    () => (modalityOptions || []).map(m => ({
      // fullLabel is the modality's full name; value/label are its code (e.g. "CT").
      label: m.fullLabel && m.fullLabel !== m.value ? `${m.fullLabel} (${m.value})` : m.value,
      value: m.value,
    })),
    [modalityOptions],
  );

  // Distinct modality codes present in the current data, for the filter dropdown.
  const modalityFilterOptions = useMemo(() => {
    const set = new Set();
    (bodyPartsList.data || []).forEach(bp => { if (bp.modality) set.add(bp.modality); });
    return Array.from(set).sort();
  }, [bodyPartsList.data]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      bp_label: record.label,
      bp_code: record.value,
      bp_modality: record.modality,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      // upsertBodyPart validates label/value/modality on every call, so echo them back.
      const res = await makePostCall('/body-parts', {
        id: record.id,
        label: record.label,
        value: record.value,
        modality: record.modality,
        is_active: checked,
      });
      if (res?.data?.success) {
        message.success(`Body part ${checked ? 'activated' : 'deactivated'}`);
        fetchBodyParts();
      } else {
        throw new Error(res?.data?.message || 'Failed to update body part');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update body part');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingRecord ? { id: editingRecord.id } : {}),
        label: values.bp_label?.trim(),
        value: String(values.bp_code || '').trim().toUpperCase(),
        modality: values.bp_modality,
        is_active: values.is_active,
      };

      const res = await makePostCall('/body-parts', payload);
      if (res?.data?.success) {
        message.success(`Body part ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchBodyParts();
      } else {
        throw new Error(res?.data?.message || 'Failed to save body part');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save body part');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setModalityFilter('all');
  };

  const filteredBodyParts = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (bodyPartsList.data || []).filter(bp => {
      if (statusFilter === 'active' && !bp.is_active) return false;
      if (statusFilter === 'inactive' && bp.is_active) return false;
      if (modalityFilter !== 'all' && bp.modality !== modalityFilter) return false;
      if (search) {
        const haystack = `${bp.label} ${bp.value} ${bp.modality}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [bodyPartsList.data, statusFilter, modalityFilter, searchText]);

  const columns = [
    {
      title: 'Body Part',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
    },
    {
      title: 'Code',
      dataIndex: 'value',
      key: 'value',
      sorter: (a, b) => (a.value || '').localeCompare(b.value || ''),
      render: (code) => <Tag>{code}</Tag>,
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      sorter: (a, b) => (a.modality || '').localeCompare(b.modality || ''),
      render: (modality) => (modality ? <Tag color="blue">{modality}</Tag> : <span style={{ color: '#94a3b8' }}>—</span>),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Edit Body Part">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Body Parts Master</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Body Part
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by name, code or modality"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select value={modalityFilter} onChange={setModalityFilter} style={{ width: 160 }}>
            <Option value="all">All Modalities</Option>
            {modalityFilterOptions.map(code => (
              <Option key={code} value={code}>{code}</Option>
            ))}
          </Select>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {bodyPartsList.error && (
        <div className="mc-error">
          <p>{bodyPartsList.error}</p>
          <Button onClick={fetchBodyParts}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Body Parts</span>
          <span className="mc-count">{filteredBodyParts.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredBodyParts}
            loading={bodyPartsList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} body parts`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Body Part' : 'Add Body Part'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Body Part Name"
            name="bp_label"
            rules={[
              { required: true, message: 'Please enter the body part name' },
              { max: 50, message: 'Name must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. Chest" />
          </Form.Item>

          <Form.Item
            label="Body Part Code"
            name="bp_code"
            tooltip={editingRecord ? 'Body part code cannot be changed once created' : undefined}
            rules={[
              { required: true, message: 'Please enter the body part code' },
              { max: 60, message: 'Code must be 60 characters or less' },
            ]}
          >
            <Input
              placeholder="e.g. CHEST"
              disabled={!!editingRecord}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            label="Modality"
            name="bp_modality"
            tooltip="The modality this body part belongs to. A code can repeat across modalities."
            rules={[{ required: true, message: 'Please select a modality' }]}
          >
            <Select
              placeholder="Select a modality"
              options={modalitySelectOptions}
              loading={modalitiesLoading}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BodyPartsMaster;
